// Seances enregistrees de l'utilisateur et bibliotheque de son club
// (SeanceController cote serveur : GET/POST /api/seances,
// PUT/DELETE /api/seances/{id}, POST /api/seances/{id}/dupliquer|publier|retirer-du-socle).
import { type LigneDetail, type Synthese } from './seanceEngine.ts';

export type Seance = {
  id?: string;
  nom: string;
  notation: string;
  notes?: string | null;
  // Synthese calculee a l'enregistrement (tri / affichage de la liste).
  vma?: number | null;
  dureeTotaleS?: number | null;
  distanceTotaleM?: number | null;
  chargeUA?: number | null;
  rpe?: number | null;
  zonePrincipale?: string | null;
  detailBlocs?: string | null;
  // Type de seance (cf classification.ts) ; typeForce = corrige a la main.
  typeSeance?: string | null;
  typeForce?: boolean | null;
  // Categorie (cf composition.ts) : CORPS par defaut, ECHAUFFEMENT,
  // RECUPERATION ou MODELE ; un MODELE n'a pas de notation (chaine vide
  // cote front) mais reference son echauffement et sa recuperation.
  categorie?: string | null;
  idEchauffement?: string | null;
  idRecuperation?: string | null;
  // Corps de seance : modele force (REPETITIONS, CONTINU, AUCUN) ; null =
  // automatique selon le type (cf composition.ts usageDe).
  modeleSeance?: string | null;
  // Modele : distance de course (cf distanceCourse.ts) ; null = toutes distances.
  distanceCourse?: string | null;
  // PERSO (seance du coach) ou SOCLE (socle commun de son groupe de coachs).
  portee?: Portee | null;
  // Modifiable par l'utilisateur (auteur, ou bibliothecaire du club pour sa bibliotheque).
  modifiable?: boolean | null;
  // Copie personnelle : seance du socle d'origine.
  idOrigine?: string | null;
  dateCreation?: string | null;
  dateModification?: string | null;
};

export type Portee = 'PERSO' | 'SOCLE';

export function duSocle(seance: Seance): boolean {
  return seance.portee === 'SOCLE';
}

/** Seance que l'utilisateur ne peut pas modifier (bibliotheque du club, sans en etre bibliothecaire). */
export function enLectureSeule(seance: Seance): boolean {
  return seance.modifiable === false;
}

function entetes(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
  };
}

async function verifier(response: Response, attendus: number[]): Promise<Response> {
  if (attendus.includes(response.status)) return response;
  let message = 'statut ' + response.status;
  if (response.status === 404) message = 'séance introuvable';
  try {
    const erreur = await response.json();
    if (erreur?.message) message = erreur.message;
  } catch {
    // corps vide ou non JSON
  }
  throw new Error(message);
}

export async function listerSeances(): Promise<Seance[]> {
  const response = await verifier(await fetch('/api/seances', { headers: entetes() }), [200]);
  // Notation nulle (modele) : chaine vide, pour ne pas avoir a la tester partout.
  return ((await response.json()) as Seance[]).map((s) => ({ ...s, notation: s.notation ?? '' }));
}

export async function enregistrerSeance(seance: Seance): Promise<Seance> {
  const response = seance.id
    ? await fetch('/api/seances/' + encodeURIComponent(seance.id), { method: 'PUT', headers: entetes(), body: JSON.stringify(seance) })
    : await fetch('/api/seances', { method: 'POST', headers: entetes(), body: JSON.stringify(seance) });
  await verifier(response, [200, 201]);
  const enregistree = (await response.json()) as Seance;
  return { ...enregistree, notation: enregistree.notation ?? '' };
}

/** Copie personnelle d'une seance (en general du socle commun), pour l'adapter. */
export async function dupliquerSeance(id: string): Promise<Seance> {
  const response = await verifier(
    await fetch('/api/seances/' + encodeURIComponent(id) + '/dupliquer', { method: 'POST', headers: entetes() }),
    [201],
  );
  const copie = (await response.json()) as Seance;
  return { ...copie, notation: copie.notation ?? '' };
}

/** Publie une seance personnelle dans la bibliotheque du club (bibliothecaire). */
export async function publierSeance(id: string): Promise<Seance> {
  const response = await verifier(
    await fetch('/api/seances/' + encodeURIComponent(id) + '/publier', { method: 'POST', headers: entetes() }),
    [200],
  );
  const publiee = (await response.json()) as Seance;
  return { ...publiee, notation: publiee.notation ?? '' };
}

/**
 * Retire une seance de la bibliotheque du club (bibliothecaire) : elle redevient une seance
 * personnelle. Refuse tant que d'autres coachs s'en servent.
 */
export async function retirerDuSocle(id: string): Promise<Seance> {
  const response = await verifier(
    await fetch('/api/seances/' + encodeURIComponent(id) + '/retirer-du-socle', { method: 'POST', headers: entetes() }),
    [200],
  );
  const retiree = (await response.json()) as Seance;
  return { ...retiree, notation: retiree.notation ?? '' };
}

/**
 * Avertissement avant de supprimer une seance ou de la retirer du socle :
 * seances posees avec elle aujourd'hui ou a venir, dans tous les calendriers
 * (elles restent en place mais seront signalees). Vide si aucune ou si le
 * decompte est indisponible.
 */
export async function avertissementCalendriers(id: string): Promise<string> {
  try {
    const response = await fetch('/api/seances/' + encodeURIComponent(id) + '/utilisations', { headers: entetes() });
    if (response.status !== 200) return '';
    const { seances, calendriers } = (await response.json()) as { seances: number; calendriers: number };
    if (!seances) return '';
    const posees = seances > 1 ? `${seances} fois` : 'une fois';
    const ou = calendriers > 1 ? `dans ${calendriers} calendriers` : 'dans un calendrier';
    return `\n\n⚠ Elle est posée ${posees} ${ou} à partir d'aujourd'hui. Ces séances restent en place, mais seront signalées comme ne correspondant plus à une séance existante.`;
  } catch {
    return '';
  }
}

export async function supprimerSeance(id: string): Promise<void> {
  await verifier(await fetch('/api/seances/' + encodeURIComponent(id), { method: 'DELETE', headers: entetes() }), [204]);
}

/**
 * Zone principale d'une seance : celle qui cumule le plus de temps d'effort
 * (temps d'effort x repetitions), recuperations exclues. Un effort progressif
 * ("Allure semi → Allure 10 km") compte pour moitie dans chacune des deux zones.
 */
export function zonePrincipale(detail: LigneDetail[]): string | null {
  const parZone = new Map<string, number>();
  detail.forEach((ligne) => {
    if (ligne.erreur || !ligne.nomZone) return;
    const zones = ligne.nomZone.split(' → ');
    const effort = ((ligne.effortTimeS ?? 0) * (ligne.nbReps ?? 1)) / zones.length;
    zones.forEach((zone) => parZone.set(zone, (parZone.get(zone) ?? 0) + effort));
  });
  let meilleure: string | null = null;
  let max = -1;
  parZone.forEach((effort, zone) => {
    if (effort > max) {
      max = effort;
      meilleure = zone;
    }
  });
  return meilleure;
}

/** Synthese a memoriser avec la seance. */
export function syntheseAEnregistrer(synthese: Synthese, detail: LigneDetail[], vma: number): Partial<Seance> {
  return {
    vma,
    dureeTotaleS: Math.round(synthese.dureeTotaleS),
    distanceTotaleM: Math.round(synthese.distanceTotaleM),
    chargeUA: Math.round(synthese.chargeUA), // UA : entier
    rpe: synthese.rpeSeance,
    zonePrincipale: zonePrincipale(detail),
    detailBlocs: synthese.detailBlocs,
  };
}
